import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Lock, LogOut, RefreshCw, Mail, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import { useSEO } from "@/lib/seo";
import { Reveal } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sf_admin_token";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function InboxPage() {
  useSEO(
    "Boîte de réception — Sculpteur Français",
    "Espace privé de lecture des messages reçus via le formulaire de contact.",
    null,
    { noindex: true }
  );

  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [messages, setMessages] = useState(null);
  const [inscriptions, setInscriptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState("");

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${t}` };
      const [msgs, inscs] = await Promise.all([
        axios.get(`${API}/contact`, { headers }),
        axios.get(`${API}/annuaire`, { headers }),
      ]);
      setMessages(msgs.data);
      setInscriptions(inscs.data);
    } catch {
      sessionStorage.removeItem(TOKEN_KEY);
      setToken("");
      setMessages(null);
      setInscriptions(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Connexion réussie.");
    } catch (err) {
      const d = err.response?.data?.detail;
      setLoginError(typeof d === "string" ? d : "Connexion impossible, réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const [showArchived, setShowArchived] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const toggleArchive = async (kind, id, archived) => {
    setBusyId(id);
    try {
      await axios.patch(`${API}/${kind}/${id}`, { archived }, { headers: { Authorization: `Bearer ${token}` } });
      const upd = (list) => list.map((x) => (x.id === id ? { ...x, archived } : x));
      if (kind === "contact") setMessages((m) => upd(m));
      else setInscriptions((m) => upd(m));
      toast.success(archived ? "Message archivé." : "Message restauré.");
    } catch {
      toast.error("Action impossible, réessayez.");
    } finally {
      setBusyId(null);
    }
  };

  const active = (list) => (list || []).filter((x) => !x.archived);
  const archived = (list) => (list || []).filter((x) => x.archived);
  const archivedTotal = archived(messages).length + archived(inscriptions).length;

  const actionBtnCls =
    "inline-flex items-center gap-2 border border-ink/20 px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-ink transition-colors hover:border-bronze hover:text-bronze disabled:opacity-50";

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setMessages(null);
    toast.success("Session fermée.");
  };

  const inputCls =
    "w-full border border-ink/20 bg-paper px-4 py-3.5 text-base font-light text-ink outline-none transition-colors duration-300 placeholder:text-smoke/50 focus:border-bronze";

  return (
    <main data-testid="inbox-page" className="min-h-[80vh] bg-paper pb-24 pt-28 sm:pt-36">
      <div className="mx-auto max-w-4xl px-6 lg:px-10">
        {!token ? (
          <Reveal>
            <div className="mx-auto max-w-md border border-ink/15 bg-cream p-8 lg:p-12" data-testid="inbox-login-card">
              <div className="flex h-12 w-12 items-center justify-center border border-bronze/40 text-bronze">
                <Lock className="h-5 w-5" />
              </div>
              <h1 className="mt-6 font-serif text-3xl font-light text-ink">Boîte de réception</h1>
              <p className="mt-3 text-sm font-light leading-relaxed text-smoke">
                Espace privé réservé à l'éditeur du site. Identifiez-vous pour
                lire les messages reçus via le formulaire de contact.
              </p>
              <form onSubmit={login} data-testid="inbox-login-form" className="mt-8">
                <label htmlFor="inbox-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-smoke">
                  Email
                </label>
                <input
                  id="inbox-email"
                  data-testid="inbox-email-input"
                  type="email"
                  required
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.fr"
                  className={inputCls}
                />
                <label htmlFor="inbox-password" className="mb-2 mt-5 block font-mono text-[11px] uppercase tracking-[0.18em] text-smoke">
                  Mot de passe
                </label>
                <input
                  id="inbox-password"
                  data-testid="inbox-password-input"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={inputCls}
                />
                {loginError && (
                  <p data-testid="inbox-login-error" className="mt-4 border-l-2 border-bronze pl-4 text-sm text-darkbronze">
                    {loginError}
                  </p>
                )}
                <button
                  type="submit"
                  data-testid="inbox-login-submit"
                  disabled={loading}
                  className="mt-7 w-full bg-ink px-7 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-paper transition-colors duration-300 hover:bg-bronze disabled:opacity-50"
                >
                  {loading ? "Connexion…" : "Accéder aux messages"}
                </button>
              </form>
            </div>
          </Reveal>
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="eyebrow">Espace privé</p>
                <h1 className="mt-3 font-serif text-4xl font-light text-ink">Boîte de réception</h1>
                {messages !== null && inscriptions !== null && (
                  <p className="mt-2 text-sm font-light text-smoke" data-testid="inbox-unread-counts">
                    {active(messages).length} message{active(messages).length > 1 ? "s" : ""} non archivé{active(messages).length > 1 ? "s" : ""} ·{" "}
                    {active(inscriptions).length} demande{active(inscriptions).length > 1 ? "s" : ""} d'inscription non archivée{active(inscriptions).length > 1 ? "s" : ""}
                  </p>
                )}
                {archivedTotal > 0 && (
                  <button
                    onClick={() => setShowArchived((s) => !s)}
                    data-testid="inbox-toggle-archived"
                    className="link-underline mt-2 font-mono text-[11px] uppercase tracking-[0.15em] text-bronze"
                  >
                    {showArchived ? "Masquer les messages archivés" : `Voir les messages archivés (${archivedTotal})`}
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => load(token)}
                  data-testid="inbox-refresh-button"
                  className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-ink transition-colors hover:border-bronze hover:text-bronze"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                  Actualiser
                </button>
                <button
                  onClick={logout}
                  data-testid="inbox-logout-button"
                  className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-ink transition-colors hover:border-bronze hover:text-bronze"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  Quitter
                </button>
              </div>
            </div>

            {messages === null ? (
              <p className="mt-12 text-sm font-light text-smoke" data-testid="inbox-loading">Chargement des messages…</p>
            ) : active(messages).length === 0 ? (
              <p className="mt-12 font-serif text-2xl text-smoke" data-testid="inbox-empty">
                Aucun message non archivé.
              </p>
            ) : (
              <div className="mt-10" data-testid="inbox-list">
                <div className="mt-5 divide-y divide-ink/10 border-y border-ink/10">
                  {active(messages).map((m) => (
                    <article key={m.id} className="py-7" data-testid={`inbox-message-${m.id}`}>
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <h2 className="font-serif text-2xl text-ink">{m.subject}</h2>
                        <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-smoke">
                          {formatDate(m.created_at)}
                        </span>
                      </div>
                      <p className="mt-2 flex flex-wrap items-center gap-2 text-sm text-smoke">
                        <Mail className="h-3.5 w-3.5 text-bronze" />
                        <span className="font-medium text-ink">{m.name}</span>
                        <a href={`mailto:${m.email}`} data-testid={`inbox-mailto-${m.id}`} className="link-underline text-bronze">
                          {m.email}
                        </a>
                      </p>
                      <p className="mt-4 max-w-2xl whitespace-pre-line text-base font-light leading-relaxed text-body">
                        {m.message}
                      </p>
                      <div className="mt-4 flex flex-wrap gap-3">
                        <a
                          href={`mailto:${m.email}?subject=${encodeURIComponent("Re : " + m.subject)}`}
                          data-testid={`inbox-reply-${m.id}`}
                          className={actionBtnCls}
                        >
                          <Mail className="h-3.5 w-3.5" />
                          Répondre
                        </a>
                        <button
                          onClick={() => toggleArchive("contact", m.id, true)}
                          disabled={busyId === m.id}
                          data-testid={`inbox-archive-${m.id}`}
                          className={actionBtnCls}
                        >
                          <Archive className="h-3.5 w-3.5" />
                          Archiver
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}

            {inscriptions !== null && (
              <div className="mt-16" data-testid="inbox-annuaire-list">
                <h2 className="font-serif text-3xl font-light text-ink">Inscriptions à l'annuaire</h2>
                {active(inscriptions).length === 0 ? (
                  <p className="mt-6 text-sm font-light text-smoke">Aucune demande d'inscription non archivée.</p>
                ) : (
                  <div className="mt-5 divide-y divide-ink/10 border-y border-ink/10">
                    {active(inscriptions).map((ins) => (
                      <article key={ins.id} className="py-7" data-testid={`inbox-annuaire-${ins.id}`}>
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <h3 className="font-serif text-2xl text-ink">{ins.atelier}</h3>
                          <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-smoke">
                            {formatDate(ins.created_at)}
                          </span>
                        </div>
                        <p className="mt-2 flex flex-wrap items-center gap-2 text-sm text-smoke">
                          <Mail className="h-3.5 w-3.5 text-bronze" />
                          <span className="font-medium text-ink">{ins.metier} — {ins.ville}</span>
                          <a href={`mailto:${ins.email}`} data-testid={`inbox-annuaire-mailto-${ins.id}`} className="link-underline text-bronze">
                            {ins.email}
                          </a>
                          {ins.site && (
                            <a href={ins.site} target="_blank" rel="noopener noreferrer" data-testid={`inbox-annuaire-site-${ins.id}`} className="link-underline text-bronze">
                              {ins.site}
                            </a>
                          )}
                        </p>
                        {ins.message && (
                          <p className="mt-4 max-w-2xl whitespace-pre-line text-base font-light leading-relaxed text-body">
                            {ins.message}
                          </p>
                        )}
                        <div className="mt-4 flex flex-wrap gap-3">
                          <a
                            href={`mailto:${ins.email}?subject=${encodeURIComponent("Re : Inscription annuaire — " + ins.atelier)}`}
                            data-testid={`inbox-annuaire-reply-${ins.id}`}
                            className={actionBtnCls}
                          >
                            <Mail className="h-3.5 w-3.5" />
                            Répondre
                          </a>
                          <button
                            onClick={() => toggleArchive("annuaire", ins.id, true)}
                            disabled={busyId === ins.id}
                            data-testid={`inbox-annuaire-archive-${ins.id}`}
                            className={actionBtnCls}
                          >
                            <Archive className="h-3.5 w-3.5" />
                            Archiver
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </div>
            )}

            {showArchived && archivedTotal > 0 && (
              <div className="mt-16 opacity-70" data-testid="inbox-archived-list">
                <h2 className="font-serif text-3xl font-light text-smoke">Messages archivés</h2>
                <div className="mt-5 divide-y divide-ink/10 border-y border-ink/10">
                  {archived(messages).map((m) => (
                    <article key={m.id} className="py-6" data-testid={`inbox-archived-contact-${m.id}`}>
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <h3 className="font-serif text-xl text-smoke">
                          {m.subject} — {m.name}
                        </h3>
                        <button
                          onClick={() => toggleArchive("contact", m.id, false)}
                          disabled={busyId === m.id}
                          data-testid={`inbox-unarchive-contact-${m.id}`}
                          className={actionBtnCls}
                        >
                          <ArchiveRestore className="h-3.5 w-3.5" />
                          Désarchiver
                        </button>
                      </div>
                    </article>
                  ))}
                  {archived(inscriptions).map((ins) => (
                    <article key={ins.id} className="py-6" data-testid={`inbox-archived-annuaire-${ins.id}`}>
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <h3 className="font-serif text-xl text-smoke">
                          Inscription annuaire — {ins.atelier}
                        </h3>
                        <button
                          onClick={() => toggleArchive("annuaire", ins.id, false)}
                          disabled={busyId === ins.id}
                          data-testid={`inbox-unarchive-annuaire-${ins.id}`}
                          className={actionBtnCls}
                        >
                          <ArchiveRestore className="h-3.5 w-3.5" />
                          Désarchiver
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
